import type { Dictionary, EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { intlShape } from 'react-intl';
import type { IAppState, ILoader } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import * as Hub from '../ogm-hub';
import { fetchLocationLevelPricingEnabled } from './CustomerSettingsSlice';
import type { IProductHeader } from './schema';
import { LoadingStatus } from './schema';

export function productId(product: Hub.ProductDetails) {
	return `${product.supplierCode}|${product.productNumber}`;
}

export const productDetailsAdapter = createEntityAdapter<Hub.ProductDetails>({
	selectId: productId,
});

export const blueprintsAndRestrictionListsAdapter =
	createEntityAdapter<BlueprintsAndRestrictionListsContainer>({
		selectId: blueprintsAndRestrictionListsId,
	});

export interface FetchProductDetailsParams {
	supplierCode: string;
	productNumber: string;
}

export interface IProductDetailsState {
	details: EntityState<Hub.ProductDetails>;
	detailsLoadingStatus: LoadingStatus;
	blueprintAndRestructionLists: EntityState<BlueprintsAndRestrictionListsContainer>;
	blueprintAndRestructionListsLoadingStatus: LoadingStatus;
	nutritionalInformation: ILoader<Hub.NutritionResponse>;
	productHeader: IProductHeader;
	productDetails: ILoader<Hub.ProductDetails>;
	productHeaderPrices: Dictionary<Hub.ProductHeaderPrices>;
	selectedProductBlueprintsAndRestrictionListsContainer?: BlueprintsAndRestrictionListsContainer;
	isPricingLoading: boolean;
	showLocationLevelPricing?: boolean;
	intl: intlShape;
	selectedProductKey: string;
}

const initialState: IProductDetailsState = {
	details: productDetailsAdapter.getInitialState(),
	detailsLoadingStatus: LoadingStatus.idle,
	blueprintAndRestructionLists:
		blueprintsAndRestrictionListsAdapter.getInitialState(),
	blueprintAndRestructionListsLoadingStatus: LoadingStatus.idle,
	nutritionalInformation: null,
	productHeader: null,
	productDetails: null,
	productHeaderPrices: {},
	selectedProductBlueprintsAndRestrictionListsContainer: null,
	isPricingLoading: false,
	showLocationLevelPricing: false,
	intl: null,
	selectedProductKey: '',
};

export interface FetchBlueprintsAndRestrictionListsProductIsOnParams {
	supplierCode: string;
	productNumber: string;
}

export interface BlueprintsAndRestrictionListsContainer {
	supplierCode: string;
	productNumber: string;
	blueprints: Hub.GetBlueprintsProductIsOnForCustomerResponse[];
	productRestrictionLists: Hub.RestrictionsListIdAndName[];
	categoryRestrictionLists: Hub.RestrictionsListIdAndName[];
}

export const fetchProductDetails = createAsyncThunk(
	'productDetails/fetchProductDetails',
	async (params: FetchProductDetailsParams, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.ProductDetailsController.GetProductDetails(
					params.supplierCode,
					params.productNumber
				)
			);
			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export function blueprintsAndRestrictionListsId(
	container: BlueprintsAndRestrictionListsContainer
) {
	return `${container.supplierCode}|${container.productNumber}`;
}

export const fetchBlueprintsAndRestrictionListsProductIsOn = createAsyncThunk(
	'productDetails/fetchBlueprintsAndRestrictionListsProductIsOn',
	async (
		params: FetchBlueprintsAndRestrictionListsProductIsOnParams,
		{ getState }
	) => {
		const state = getState() as IAppState;
		const supplier =
			state.suppliers.customerSuppliersByCode[params.supplierCode];

		const blueprintRequestParams: Hub.GetBlueprintsProductIsOnForCustomerRequest =
			{
				supplierId: supplier.id,
				supplierProductNumber: params.productNumber,
			};

		const restrictionRequestParams: Hub.GetRestrictionListsProductIsOnForCustomerArgs =
			{
				supplierProductNumber: params.productNumber,
				supplierCode: params.supplierCode,
			};

		const [blueprintResult, restrictionListResult] = await Promise.all([
			unwrapFetch2(
				Hub.BlueprintControllerV3.GetBlueprintsProductIsOnForCustomer(
					blueprintRequestParams
				)
			),
			unwrapFetch2(
				Hub.RestrictionsController.GetRestrictionListsProductIsOnForCustomer(
					restrictionRequestParams
				)
			),
		]);

		const blueprints = blueprintResult.results;
		const productRestrictionLists =
			restrictionListResult.productRestrictionLists;
		const categoryRestrictionLists =
			restrictionListResult.categoryRestrictionLists;

		const result: BlueprintsAndRestrictionListsContainer = {
			supplierCode: params.supplierCode,
			productNumber: params.productNumber,
			blueprints,
			productRestrictionLists,
			categoryRestrictionLists,
		};
		return result;
	}
);

export const productDetailsSlice = createSlice({
	name: 'productDetails',
	initialState: initialState,
	reducers: {
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchProductDetails.fulfilled, (state, action) => {
			state.detailsLoadingStatus = LoadingStatus.success;
			productDetailsAdapter.upsertOne(state.details, action.payload);
		});
		builder.addCase(fetchProductDetails.rejected, (state) => {
			state.detailsLoadingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchProductDetails.pending, (state) => {
			state.detailsLoadingStatus = LoadingStatus.loading;
		});

		builder.addCase(
			fetchBlueprintsAndRestrictionListsProductIsOn.fulfilled,
			(state, action) => {
				state.blueprintAndRestructionListsLoadingStatus = LoadingStatus.success;
				blueprintsAndRestrictionListsAdapter.upsertOne(
					state.blueprintAndRestructionLists,
					action.payload
				);
				state.selectedProductBlueprintsAndRestrictionListsContainer =
					action.payload;
			}
		);
		builder.addCase(
			fetchBlueprintsAndRestrictionListsProductIsOn.rejected,
			(state) => {
				state.blueprintAndRestructionListsLoadingStatus = LoadingStatus.failure;
			}
		);
		builder.addCase(
			fetchBlueprintsAndRestrictionListsProductIsOn.pending,
			(state) => {
				state.blueprintAndRestructionListsLoadingStatus = LoadingStatus.loading;
			}
		);
		builder.addCase(
			fetchLocationLevelPricingEnabled.fulfilled,
			(state, action) => {
				state.showLocationLevelPricing =
					action.payload.locationLevelPricingEnabled;
			}
		);
	},
});
